import { useEffect, useRef, useState } from "react";
import type { OrderSample } from "@probetalab/contracts";
import { get, getHtml, run } from "../lib/api.js";
import { printHtml } from "../lib/print.js";
import { useToast } from "../lib/toast.js";
import { errorMessage } from "../lib/useQuery.js";
import { Button, IconButton, Segmented, Switch } from "./ui.js";

type Width = "58" | "80";
const WIDTH_KEY = "probeta.ticket_width";

export function useTicketWidth(): [Width, (w: Width) => void] {
  const [w, setW] = useState<Width>(() => (localStorage.getItem(WIDTH_KEY) === "58" ? "58" : "80"));
  return [
    w,
    (next) => {
      localStorage.setItem(WIDTH_KEY, next);
      setW(next);
    },
  ];
}

/** What Recepción prints by itself after «Guardar e imprimir», per device. */
export type AutoPrint = "off" | "ticket" | "ticket+labels";
const AUTO_PRINT_KEY = "probeta.auto_print";

export function readAutoPrint(): AutoPrint {
  const v = localStorage.getItem(AUTO_PRINT_KEY);
  return v === "off" || v === "ticket" ? v : "ticket+labels";
}

function useAutoPrint(): [AutoPrint, (v: AutoPrint) => void] {
  const [v, setV] = useState<AutoPrint>(readAutoPrint);
  return [
    v,
    (next) => {
      localStorage.setItem(AUTO_PRINT_KEY, next);
      setV(next);
    },
  ];
}

/**
 * Ticket + labels print buttons for an order. Labels are recorded as printed (`labels.printed`).
 * With `autoPrint` (Recepción's post-save dialog, Very 2026-11-02) the ticket prints on mount and,
 * once its print dialog closes, the labels: the two dialogs are chained, never open at once. The
 * buttons stay for reprints. When auto printing is off or the browser refuses to print, focus
 * lands on «Imprimir ticket» so Enter prints it.
 */
export function OrderPrintActions({
  orderId,
  samples,
  size = "md",
  onLabelsPrinted,
  autoPrint = false,
}: {
  orderId: string;
  samples: OrderSample[];
  size?: "md" | "lg";
  onLabelsPrinted?: (s: OrderSample[]) => void;
  autoPrint?: boolean;
}) {
  const toast = useToast();
  const [width, setWidth] = useTicketWidth();
  const [auto, setAuto] = useAutoPrint();
  const [busy, setBusy] = useState<"ticket" | "labels" | null>(null);
  const [focusTicket, setFocusTicket] = useState(false);
  const ticketRef = useRef<HTMLButtonElement>(null);
  const started = useRef(false);

  /** Resolves `true` once the print dialog has closed, `false` when nothing printed. */
  async function printTicket(): Promise<boolean> {
    setBusy("ticket");
    let printing: Promise<boolean>;
    try {
      printing = printHtml(await getHtml("orderTicketHtml", { params: { id: orderId }, query: { width } }));
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    } finally {
      setBusy(null);
    }
    return printing;
  }

  async function printLabels(): Promise<boolean> {
    setBusy("labels");
    let printing: Promise<boolean>;
    try {
      printing = printHtml(await getHtml("orderLabelsHtml", { params: { id: orderId } }));
      if (samples.length) {
        const updated = await run<"labels.printed", OrderSample[]>("labels.printed", { order_id: orderId, sample_ids: samples.map((s) => s.id) });
        onLabelsPrinted?.(updated);
      }
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    } finally {
      setBusy(null);
    }
    return printing;
  }

  // Once per mounted order (the ref survives StrictMode's double effect): ticket, then labels.
  useEffect(() => {
    if (!autoPrint || started.current) return;
    started.current = true;
    const mode = readAutoPrint();
    if (mode === "off") {
      setFocusTicket(true);
      return;
    }
    void (async () => {
      if (!(await printTicket())) {
        setFocusTicket(true);
        return;
      }
      if (mode === "ticket+labels" && samples.length) await printLabels();
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The ticket button is disabled while busy, so focus waits until it is enabled again.
  useEffect(() => {
    if (focusTicket && busy === null) {
      ticketRef.current?.focus();
      setFocusTicket(false);
    }
  }, [focusTicket, busy]);

  return (
    <div className="stack">
      <div className="row wrap" style={{ gap: 10 }}>
        <Button ref={ticketRef} variant="primary" size={size} icon="print" onClick={() => void printTicket()} busy={busy === "ticket"}>
          Imprimir ticket
        </Button>
        <Button variant="secondary" size={size} icon="tag" onClick={() => void printLabels()} busy={busy === "labels"} disabled={!samples.length}>
          Imprimir etiquetas{samples.length ? ` (${samples.length})` : ""}
        </Button>
        <Segmented small value={width} onChange={setWidth} options={[{ value: "80", label: "80 mm" }, { value: "58", label: "58 mm" }]} />
      </div>
      {autoPrint && (
        <div className="row wrap" style={{ gap: 12 }}>
          <Switch checked={auto !== "off"} onChange={(on) => setAuto(on ? "ticket+labels" : "off")}>
            Imprimir automáticamente al guardar
          </Switch>
          {auto !== "off" && (
            <Segmented
              small
              value={auto}
              onChange={setAuto}
              options={[
                { value: "ticket+labels", label: "Ticket y etiquetas" },
                { value: "ticket", label: "Solo ticket" },
              ]}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Solicitudes' per-row print buttons (Fernanda, 2026-11-02 20:10, after Toronja's green «Etiquetas»
 * and blue «Ticket»): print straight from the list without opening the order. The ticket follows
 * the width saved on this device; labels are recorded as printed like «Imprimir etiquetas» on the
 * order. Clicks and Enter stay here, so the row never navigates.
 */
export function OrderRowPrintButtons({ orderId, folio, samplesCount }: { orderId: string; folio: string; samplesCount: number }) {
  const toast = useToast();
  const [width] = useTicketWidth();
  const [busy, setBusy] = useState<"ticket" | "labels" | null>(null);

  async function printTicket() {
    setBusy("ticket");
    try {
      void printHtml(await getHtml("orderTicketHtml", { params: { id: orderId }, query: { width } }));
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function printLabels() {
    setBusy("labels");
    try {
      const [html, order] = await Promise.all([getHtml("orderLabelsHtml", { params: { id: orderId } }), get("order", { params: { id: orderId } })]);
      void printHtml(html);
      if (order.samples.length) await run<"labels.printed", OrderSample[]>("labels.printed", { order_id: orderId, sample_ids: order.samples.map((x) => x.id) });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <span className="row" style={{ gap: 2, justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <IconButton
        icon="tag"
        size="sm"
        label={`Imprimir etiquetas de ${folio}`}
        style={{ color: samplesCount ? "var(--ok)" : undefined }}
        disabled={!samplesCount || busy !== null}
        onClick={() => void printLabels()}
      />
      <IconButton icon="print" size="sm" label={`Imprimir ticket de ${folio}`} style={{ color: "var(--blue)" }} disabled={busy !== null} onClick={() => void printTicket()} />
    </span>
  );
}

export function QuotePrintButton({ quoteId, size = "md" }: { quoteId: string; size?: "md" | "lg" }) {
  const toast = useToast();
  const [width] = useTicketWidth();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      variant="secondary"
      size={size}
      icon="print"
      busy={busy}
      onClick={async () => {
        setBusy(true);
        try {
          printHtml(await getHtml("quoteTicketHtml", { params: { id: quoteId }, query: { width } }));
        } catch (e) {
          toast.error(errorMessage(e));
        } finally {
          setBusy(false);
        }
      }}
    >
      Imprimir cotización
    </Button>
  );
}

/** What a cash close print is of: the live drawer (the summary's own filters) or a recorded close. */
export type CashCloseTarget =
  | { kind: "summary"; query: { branch_id: string; user_id?: string; period_start: string; period_end: string } }
  | { kind: "close"; id: string };

/**
 * "Imprimir corte" on the ticket roll (the shared 80/58 mm choice) and "Imprimir (hoja carta)" for
 * the desktop PC (Very, 2026-11-02). `compact` drops the width toggle and shortens the labels for a
 * table row; the row still follows the width chosen on the summary.
 */
export function CashClosePrintActions({ target, compact = false, disabled = false }: { target: CashCloseTarget; compact?: boolean; disabled?: boolean }) {
  const toast = useToast();
  const [width, setWidth] = useTicketWidth();
  const [busy, setBusy] = useState<"ticket" | "letter" | null>(null);

  async function print(which: "ticket" | "letter") {
    const format = which === "letter" ? "letter" : width;
    setBusy(which);
    try {
      printHtml(
        target.kind === "summary"
          ? await getHtml("cashCloseSummaryHtml", { query: { ...target.query, format } })
          : await getHtml("cashCloseHtml", { params: { id: target.id }, query: { format } }),
      );
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  const size = compact ? "sm" : "md";
  return (
    <div className="row wrap" style={{ gap: compact ? 6 : 10 }}>
      <Button variant={compact ? "ghost" : "secondary"} size={size} icon="print" disabled={disabled} busy={busy === "ticket"} onClick={() => void print("ticket")}>
        Imprimir corte
      </Button>
      <Button variant="ghost" size={size} disabled={disabled} busy={busy === "letter"} onClick={() => void print("letter")} aria-label="Imprimir (hoja carta)">
        {compact ? "Hoja carta" : "Imprimir (hoja carta)"}
      </Button>
      {!compact && <Segmented small value={width} onChange={setWidth} options={[{ value: "80", label: "80 mm" }, { value: "58", label: "58 mm" }]} />}
    </div>
  );
}
